import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { PERMISSION } from '@lib/core/enum';
import { InputSwitchComponent } from '@proget-shared/form/input-switch';
import { GridAction, GridActionPrompt, GridActionType } from '@proget-shared/grid/grid-actions';
import { GridControlModule } from '@proget-shared/grid/grid-control';
import { TableModule } from '@proget-shared/grid/table';
import { TableActionsModule } from '@proget-shared/grid/table-actions';
import { PipeModule } from '@proget-shared/pipe';
import { TranslateModule } from '@proget-shared/translate';
import { CopyToClipboardModule } from '@proget-shared/ui/copy-to-clipboard';
import { EllipsisModule } from '@proget-shared/ui/ellipsis';
import { LinkModule } from '@proget-shared/ui/link';
import { ToastService } from '@proget-shared/ui/toast';
import { TooltipModule } from '@proget-shared/ui/tooltip';
import { combineLatest } from 'rxjs';

import { AttributeValueDialogComponent } from '../../../component/attribute-value-dialog/attribute-value-dialog.component';
import { AttributeType } from '../../../enum';
import { Attribute, User } from '../../../model';
import { UserCardService } from '../../../service/user-card.service';

@Component({
  selector: 'lib-user-certificates-list',
  templateUrl: './attributes.component.html',
  styleUrl: './attributes.component.scss',
  imports: [
    ReactiveFormsModule,
    GridControlModule,
    TableModule,
    TableActionsModule,
    InputSwitchComponent,
    PipeModule,
    TranslateModule,
    CopyToClipboardModule,
    EllipsisModule,
    LinkModule,
    TooltipModule,
  ],
})
export class AttributesComponent {
  protected readonly AttributeType = AttributeType;

  protected attributes: Attribute[];
  protected attributesActions: GridAction<Attribute>[] = [];

  private readonly destroyRef = inject(DestroyRef);
  private readonly userCardService = inject(UserCardService);
  private readonly toastService = inject(ToastService);

  private user: User;

  constructor() {
    combineLatest([
      this.userCardService.allAttributes$,
      this.userCardService.user$,
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([attributes, user]) => {
          this.user = user;
          this.attributes = attributes;
          this.listenToAttributesVisibilityChange();
          this.prepareActions();
        },
      });
  }

  private prepareActions(): void {
    this.attributesActions = [
      {
        icon: 'icon-edit-icon',
        label: 'library.user_card.attributes.change_value.header',
        permission: PERMISSION.USER_CARD_ALLOW_MANAGE,
        enabled: (item) => item.editable,
        prompt: {
          type: GridActionPrompt.CUSTOM,
          component: AttributeValueDialogComponent,
          options: {
            configuration: {
              user: this.user,
            },
          },
        },
        type: GridActionType.DISPLAY_SINGLE,
        success: {
          callback: (): void => {
            this.userCardService.reloadAttributes();
            this.toastService.success('library.user_card.attributes.change_value.toast.success');
          },
        },
      },
    ];
  }

  private listenToAttributesVisibilityChange(): void {
    for (const attribute of this.attributes) {
      attribute.isVisibleControl.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.userCardService.attributesVisibilityChanged(this.attributes);
          },
        });
    }
  }
}
